import { useContext, useEffect } from 'react';
import { PageCrumbsCtx } from './page-crumbs-context';
import type { Crumb } from './use-breadcrumbs';

/** Read the override. Used by the top bar; pages use `usePageCrumbs`. */
export function usePageCrumbsOverride(): Crumb[] | null {
  return useContext(PageCrumbsCtx)?.crumbs ?? null;
}

/**
 * Publish this page's breadcrumb trail.
 *
 * Pass `null` (or nothing) to fall back to the nav-derived trail. The trail
 * is cleared on unmount, so navigating away never leaves a stale crumb.
 *
 * Callers must memoise `crumbs` (or pass a stable literal built from
 * primitives) — the effect depends on a JSON key rather than identity so an
 * inline array does not loop.
 */
export function usePageCrumbs(crumbs: Crumb[] | null | undefined) {
  const ctx = useContext(PageCrumbsCtx);
  const setCrumbs = ctx?.setCrumbs;
  const key = crumbs ? JSON.stringify(crumbs) : null;

  useEffect(() => {
    if (!setCrumbs) return;
    setCrumbs(key ? (JSON.parse(key) as Crumb[]) : null);
    return () => setCrumbs(null);
  }, [key, setCrumbs]);
}
